import React, { useState, useEffect } from 'react';
import { Container, Spinner, Alert, Badge, Button, Card, Form, InputGroup, Modal, ProgressBar, Collapse, Table } from 'react-bootstrap';
import { format } from 'date-fns';
import { FaEdit, FaFilePdf, FaSearch, FaBox, FaCalendarAlt, FaStore, FaTruck, FaPrint, FaExclamationTriangle, FaCheckSquare, FaSquare, FaChevronDown, FaChevronUp, FaBoxOpen } from 'react-icons/fa';
import apinode from '../../services/apinode';
import { Link } from 'react-router-dom';
import Navbar from '../../components/navbar/navbar.jsx';

const PendingOrders = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [printingLabels, setPrintingLabels] = useState(false);
  const [printProgress, setPrintProgress] = useState({ current: 0, total: 0, status: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [expandedOrders, setExpandedOrders] = useState({});
  const [orderDetails, setOrderDetails] = useState({});
  const [loadingDetails, setLoadingDetails] = useState({});

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await apinode.get(`/pedido/notchecked/${localStorage.idconta}`);
        setOrders(response.data);
        setFilteredOrders(response.data);
        setLoading(false);
      } catch (error) {
        setError(error);
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredOrders(orders);
      return;
    }

    const filtered = orders.filter(order => {
      const searchLower = searchTerm.toLowerCase();
      const pedido = String(order.pedido || '').toLowerCase();
      const cliente = String(order.cliente || '').toLowerCase();
      const plataforma = String(order.plataforma || '').toLowerCase();
      const status = String(order.status || '').toLowerCase();
      const apelido = String(order.apelido || '').toLowerCase();
      
      return pedido.includes(searchLower) ||
             cliente.includes(searchLower) ||
             plataforma.includes(searchLower) ||
             status.includes(searchLower) ||
             apelido.includes(searchLower);
    });
    
    setFilteredOrders(filtered);
    setCurrentPage(1);
  }, [searchTerm, orders]);

  // Paginação
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentOrders = filteredOrders.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return format(date, 'dd/MM/yyyy');
    } catch {
      return '-';
    }
  };

  const formatCurrency = (value) => {
    if (!value) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const toggleOrderDetails = async (pedido) => {
    const isExpanded = expandedOrders[pedido];
    
    setExpandedOrders(prev => ({
      ...prev,
      [pedido]: !isExpanded
    }));

    // Se está expandindo e ainda não tem os detalhes, buscar
    if (!isExpanded && !orderDetails[pedido]) {
      setLoadingDetails(prev => ({ ...prev, [pedido]: true }));
      
      try {
        const response = await apinode.get(`/orderdetails/${pedido}/${localStorage.idconta}`);
        setOrderDetails(prev => ({
          ...prev,
          [pedido]: response.data
        }));
      } catch (error) {
        console.error('Erro ao buscar detalhes do pedido:', error);
        showAlert('error', 'Erro ao carregar detalhes do pedido.');
      } finally {
        setLoadingDetails(prev => ({ ...prev, [pedido]: false }));
      }
    }
  };

  const handleNfe = (loja, idnfe) => {
    const linkUrl = `/nfe/editar/${idnfe}`;
    window.open(linkUrl, '_blank');
  };

  const toggleOrderSelection = (pedido) => {
    setSelectedOrders(prev => {
      if (prev.includes(pedido)) {
        return prev.filter(p => p !== pedido);
      } else {
        return [...prev, pedido];
      }
    });
  };

  const toggleSelectAll = () => {
    if (selectedOrders.length === currentOrders.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(currentOrders.map(order => order.pedido));
    }
  };

  const handlePrintLabelsClick = () => {
    if (selectedOrders.length === 0) {
      showAlert('error', 'Selecione pelo menos um pedido para imprimir as etiquetas.');
      return;
    }
    setShowWarningModal(true);
  };

  const handleConfirmPrint = async () => {
    setShowWarningModal(false);
    setPrintingLabels(true);

    const ordersToProcess = orders.filter(order => selectedOrders.includes(order.pedido));
    setPrintProgress({ current: 0, total: ordersToProcess.length, status: 'Iniciando...' });

    let successCount = 0;
    let errorCount = 0;
    const errors = [];

    for (let i = 0; i < ordersToProcess.length; i++) {
      const order = ordersToProcess[i];
      
      setPrintProgress({
        current: i + 1,
        total: ordersToProcess.length,
        status: `Processando pedido #${order.pedido}...`
      });

      try {
        const response = await apinode.get(
          `/pedido/shippinglabelsMl/${localStorage.idconta}`,
          {
            params: {
              seller: order.id_loja,
              shipping: order.ship,
              pedido: order.pedido
            }
          }
        );

        if (response.data.success) {
          successCount++;
        } else {
          errorCount++;
          errors.push(`Pedido #${order.pedido}: Falha na impressão`);
        }
      } catch (error) {
        errorCount++;
        const errorMessage = error.response?.data?.error || 'Erro desconhecido';
        errors.push(`Pedido #${order.pedido}: ${errorMessage}`);
      }

      // Aguardar 500ms entre requisições para não sobrecarregar o servidor
      if (i < ordersToProcess.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }

    setPrintingLabels(false);
    setPrintProgress({ current: 0, total: 0, status: '' });
    setSelectedOrders([]);

    // Mostrar resultado final
    if (errorCount === 0) {
      showAlert('success', `${successCount} etiqueta(s) enviada(s) para impressão com sucesso!`);
    } else if (successCount === 0) {
      showAlert('error', `Falha ao imprimir todas as etiquetas. Erros: ${errors.join('; ')}`);
    } else {
      showAlert('warning', `${successCount} etiqueta(s) impressa(s) com sucesso. ${errorCount} falha(s). Erros: ${errors.join('; ')}`);
    }
  };

  const showAlert = (type, message) => {
    const variantMap = {
      'success': 'success',
      'error': 'danger',
      'warning': 'warning'
    };
    const variant = variantMap[type] || 'info';
    
    const alertDiv = document.createElement('div');
    alertDiv.className = `alert alert-${variant} alert-dismissible fade show position-fixed top-0 start-50 translate-middle-x mt-3`;
    alertDiv.style.zIndex = '9999';
    alertDiv.style.minWidth = '400px';
    alertDiv.style.maxWidth = '600px';
    alertDiv.innerHTML = `
      ${message}
      <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
    `;
    document.body.appendChild(alertDiv);
    setTimeout(() => alertDiv.remove(), type === 'success' ? 3000 : 8000);
  };

  const renderStatusBadge = (status) => {
    const statusConfig = {
      'Pendente': { bg: 'warning', text: 'dark' },
      'Atendido': { bg: 'success', text: 'white' },
      'Cancelado': { bg: 'danger', text: 'white' },
      'Em aberto': { bg: 'info', text: 'white' },
      'Ag. conf.': { bg: 'primary', text: 'white' },
      'Aguardando': { bg: 'secondary', text: 'white' }
    };

    const config = statusConfig[status] || { bg: 'secondary', text: 'white' };
    
    return (
      <Badge 
        bg={config.bg} 
        text={config.text}
        className="px-3 py-2 fw-normal"
        style={{ fontSize: '0.85rem', letterSpacing: '0.3px' }}
      >
        {status}
      </Badge>
    );
  };

  const renderOrderItems = (pedido) => {
    const details = orderDetails[pedido];
    const isLoading = loadingDetails[pedido];

    if (isLoading) {
      return (
        <div className="text-center py-4">
          <Spinner animation="border" size="sm" variant="primary" />
          <p className="text-muted mt-2 mb-0">Carregando itens...</p>
        </div>
      );
    }

    if (!details) {
      return (
        <div className="text-center py-4">
          <p className="text-muted mb-0">Nenhum item encontrado</p>
        </div>
      );
    }

    const pedidoData = details.pedido_int?.[0];
    const items = details.itempedido || [];

    return (
      <div className="border-top mt-3 pt-3">
        {/* Informações do Pedido */}
        {pedidoData && (
          <div className="row mb-3">
            <div className="col-md-6">
              <h6 className="text-primary mb-3">Informações do Pedido</h6>
              <div className="mb-2">
                <small className="text-muted">ID Pedido Loja:</small>
                <div className="fw-semibold">{pedidoData.IDPEDIDOLOJA || '-'}</div>
              </div>
              <div className="mb-2">
                <small className="text-muted">CPF/Telefone:</small>
                <div className="fw-semibold">{pedidoData.CPF || '-'}</div>
              </div>
              <div className="mb-2">
                <small className="text-muted">Valor Total:</small>
                <div className="fw-semibold text-success">{formatCurrency(pedidoData.TOTALPEDIDO)}</div>
              </div>
            </div>
            <div className="col-md-6">
              <h6 className="text-primary mb-3">Endereço de Entrega</h6>
              <div className="mb-1">
                <small className="text-muted">Endereço:</small>
                <div>{pedidoData.ENT_RUA}, {pedidoData.ENT_NUM}</div>
              </div>
              <div className="mb-1">
                <small className="text-muted">Bairro:</small>
                <div>{pedidoData.ENT_BAIRRO}</div>
              </div>
              <div className="mb-1">
                <small className="text-muted">Cidade/UF:</small>
                <div>{pedidoData.ENT_CIDADE} - {pedidoData.ENT_UF}</div>
              </div>
              <div className="mb-1">
                <small className="text-muted">CEP:</small>
                <div>{pedidoData.ENT_CEP}</div>
              </div>
              {pedidoData.DETALHES_PEDIDO && (
                <div className="mt-2">
                  <small className="text-muted">Complemento:</small>
                  <div className="small">{pedidoData.DETALHES_PEDIDO}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Itens do Pedido */}
        {items.length > 0 && (
          <div>
            <h6 className="text-primary mb-3">Itens do Pedido ({items.length})</h6>
            <div className="table-responsive">
              <Table hover size="sm" className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Código</th>
                    <th>Descrição</th>
                    <th className="text-center">Qtd</th>
                    <th className="text-end">Valor Unit.</th>
                    <th className="text-end">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => (
                    <tr key={index}>
                      <td className="align-middle">
                        <code>{item.CODIGO}</code>
                      </td>
                      <td className="align-middle">
                        <div className="text-truncate" style={{ maxWidth: '300px' }} title={item.DESCRICAO}>
                          {item.DESCRICAO}
                        </div>
                        {item.SKU && (
                          <small className="text-muted">SKU: {item.SKU}</small>
                        )}
                      </td>
                      <td className="text-center align-middle">
                        <Badge bg="secondary">{item.QUANTIDADE}</Badge>
                      </td>
                      <td className="text-end align-middle">
                        {formatCurrency(item.VALORUNITARIO)}
                      </td>
                      <td className="text-end align-middle fw-semibold">
                        {formatCurrency(item.VALOR_TOTAL_ITEM)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="table-light">
                  <tr>
                    <td colSpan="4" className="text-end fw-bold">Total:</td>
                    <td className="text-end fw-bold text-success">
                      {formatCurrency(items.reduce((sum, item) => sum + (item.VALOR_TOTAL_ITEM || 0), 0))}
                    </td>
                  </tr>
                </tfoot>
              </Table>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pageNumbers = [];
    const maxPagesToShow = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    
    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <nav aria-label="Navegação de página">
        <ul className="pagination justify-content-center mb-0">
          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
            <button 
              className="page-link" 
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Anterior
            </button>
          </li>
          
          {startPage > 1 && (
            <>
              <li className="page-item">
                <button className="page-link" onClick={() => handlePageChange(1)}>1</button>
              </li>
              {startPage > 2 && <li className="page-item disabled"><span className="page-link">...</span></li>}
            </>
          )}

          {pageNumbers.map(number => (
            <li key={number} className={`page-item ${currentPage === number ? 'active' : ''}`}>
              <button 
                className="page-link" 
                onClick={() => handlePageChange(number)}
              >
                {number}
              </button>
            </li>
          ))}

          {endPage < totalPages && (
            <>
              {endPage < totalPages - 1 && <li className="page-item disabled"><span className="page-link">...</span></li>}
              <li className="page-item">
                <button className="page-link" onClick={() => handlePageChange(totalPages)}>{totalPages}</button>
              </li>
            </>
          )}

          <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
            <button 
              className="page-link" 
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Próximo
            </button>
          </li>
        </ul>
      </nav>
    );
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <Container fluid className="d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
          <div className="text-center">
            <Spinner animation="border" role="status" variant="primary" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Carregando...</span>
            </Spinner>
            <p className="mt-3 text-muted">Carregando pedidos...</p>
          </div>
        </Container>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Navbar />
        <Container className="mt-5">
          <Alert variant="danger" className="shadow-sm">
            <Alert.Heading>Erro ao carregar pedidos</Alert.Heading>
            <p>Houve um problema ao obter os pedidos. Por favor, tente novamente mais tarde.</p>
          </Alert>
        </Container>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <Container fluid className="py-4" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
        <Container>
          {/* Header Section */}
          <div className="mb-4">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div className="d-flex align-items-center">
                <FaBox className="me-3" style={{ fontSize: '2rem', color: '#0d6efd' }} />
                <div>
                  <h2 className="mb-0 fw-bold" style={{ color: '#2c3e50' }}>Pedidos Pendentes</h2>
                  <p className="text-muted mb-0">Gerencie os pedidos aguardando envio</p>
                </div>
              </div>
              
              {/* Botão Imprimir Selecionados */}
              {selectedOrders.length > 0 && (
                <Button 
                  variant="success" 
                  onClick={handlePrintLabelsClick}
                  className="d-flex align-items-center"
                  disabled={printingLabels}
                >
                  <FaPrint className="me-2" />
                  Imprimir {selectedOrders.length} Etiqueta(s)
                </Button>
              )}
            </div>

            {/* Search Bar */}
            <Card className="shadow-sm border-0 mb-4">
              <Card.Body>
                <InputGroup>
                  <InputGroup.Text className="bg-white border-end-0">
                    <FaSearch className="text-muted" />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Buscar por pedido, cliente, plataforma, status ou apelido..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="border-start-0 ps-0"
                    style={{ boxShadow: 'none' }}
                  />
                </InputGroup>
              </Card.Body>
            </Card>

            {/* Stats & Select All */}
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="d-flex gap-2">
                <Badge bg="primary" className="px-3 py-2">
                  Total: {filteredOrders.length} pedidos
                </Badge>
                {selectedOrders.length > 0 && (
                  <Badge bg="success" className="px-3 py-2">
                    Selecionados: {selectedOrders.length}
                  </Badge>
                )}
              </div>
              
              {currentOrders.length > 0 && (
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={toggleSelectAll}
                  className="d-flex align-items-center"
                >
                  {selectedOrders.length === currentOrders.length ? (
                    <>
                      <FaCheckSquare className="me-2" />
                      Desmarcar Todos
                    </>
                  ) : (
                    <>
                      <FaSquare className="me-2" />
                      Selecionar Todos
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>

          {/* Progress Bar */}
          {printingLabels && (
            <Card className="shadow-sm border-0 mb-4">
              <Card.Body>
                <h6 className="mb-3">Imprimindo Etiquetas...</h6>
                <ProgressBar 
                  now={(printProgress.current / printProgress.total) * 100} 
                  label={`${printProgress.current}/${printProgress.total}`}
                  animated
                  className="mb-2"
                />
                <small className="text-muted">{printProgress.status}</small>
              </Card.Body>
            </Card>
          )}

          {/* Orders List */}
          {filteredOrders.length === 0 ? (
            <Card className="shadow-sm border-0 text-center py-5">
              <Card.Body>
                <FaBox style={{ fontSize: '4rem', color: '#dee2e6' }} className="mb-3" />
                <h5 className="text-muted">Nenhum pedido encontrado</h5>
                <p className="text-muted">
                  {searchTerm ? 'Tente ajustar os termos de busca.' : 'Não há pedidos pendentes no momento.'}
                </p>
              </Card.Body>
            </Card>
          ) : (
            <>
              <div className="row g-3 mb-4">
                {currentOrders.map((order) => (
                  <div key={order.pedido} className="col-12">
                    <Card 
                      className={`shadow-sm border hover-shadow ${selectedOrders.includes(order.pedido) ? 'border-primary border-2' : 'border-0'}`}
                      style={{ transition: 'all 0.3s ease' }}
                    >
                      <Card.Body className="p-4">
                        <div className="row align-items-center">
                          {/* Checkbox */}
                          <div className="col-auto">
                            <Form.Check
                              type="checkbox"
                              checked={selectedOrders.includes(order.pedido)}
                              onChange={() => toggleOrderSelection(order.pedido)}
                              style={{ transform: 'scale(1.3)' }}
                            />
                          </div>

                          {/* Order Number & Status */}
                          <div className="col-lg-2 col-md-6 mb-3 mb-lg-0">
                            <div className="d-flex align-items-center">
                              <div className="bg-primary bg-opacity-10 rounded p-3 me-3">
                                <FaBox className="text-primary" style={{ fontSize: '1.5rem' }} />
                              </div>
                              <div>
                                <small className="text-muted d-block">Pedido</small>
                                <strong className="fs-5" style={{ color: '#2c3e50' }}>#{order.pedido}</strong>
                                <div className="mt-1">
                                  {renderStatusBadge(order.status)}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Customer Info */}
                          <div className="col-lg-2 col-md-6 mb-3 mb-lg-0">
                            <small className="text-muted d-block mb-1">Cliente</small>
                            <div className="fw-semibold text-capitalize" style={{ color: '#495057' }}>
                              {order.cliente ? String(order.cliente).toLowerCase() : '-'}
                            </div>
                            <small className="text-muted">
                              <FaStore className="me-1" />
                              <span className="text-capitalize">{order.plataforma ? String(order.plataforma).toLowerCase() : '-'}</span>
                            </small>
                          </div>

                          {/* Apelido */}
                          <div className="col-lg-2 col-md-6 mb-3 mb-lg-0">
                            <small className="text-muted d-block mb-1">Loja/Apelido</small>
                            <div className="fw-semibold" style={{ color: '#495057' }}>
                              {order.apelido || '-'}
                            </div>
                            <small className="text-muted">
                              {order.nome_conta || '-'}
                            </small>
                          </div>

                          {/* Dates */}
                          <div className="col-lg-2 col-md-6 mb-3 mb-lg-0">
                            <div className="mb-2">
                              <small className="text-muted d-block">
                                <FaCalendarAlt className="me-1" />
                                Data do Pedido
                              </small>
                              <span className="fw-semibold">{formatDate(order.data)}</span>
                            </div>
                            <div>
                              <small className="text-muted d-block">
                                <FaTruck className="me-1" />
                                Data Despacho
                              </small>
                              <span className="fw-semibold">{formatDate(order.data_despacho)}</span>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="col-lg-3 col-md-12">
                            <div className="d-flex gap-2 justify-content-lg-end flex-wrap">
                              <Button
                                variant={expandedOrders[order.pedido] ? "primary" : "outline-secondary"}
                                size="sm"
                                onClick={() => toggleOrderDetails(order.pedido)}
                                className="d-flex align-items-center"
                              >
                                <FaBoxOpen className="me-2" />
                                {expandedOrders[order.pedido] ? <FaChevronUp /> : <FaChevronDown />}
                              </Button>
                              <Link to={`/pedidos/editar/${order.pedido}`}>
                                <Button 
                                  variant="outline-primary" 
                                  size="sm"
                                  className="d-flex align-items-center"
                                >
                                  <FaEdit className="me-2" />
                                  Editar
                                </Button>
                              </Link>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleNfe(order.id_loja, order.id_nfe)}
                                className="d-flex align-items-center"
                              >
                                <FaFilePdf className="me-2" />
                                NFE
                              </Button>
                              <Button
                                variant="outline-success"
                                size="sm"
                                onClick={() => {
                                  setSelectedOrders([order.pedido]);
                                  handlePrintLabelsClick();
                                }}
                                className="d-flex align-items-center"
                                disabled={!order.ship || !order.id_loja || printingLabels}
                              >
                                <FaPrint className="me-2" />
                                Etiqueta
                              </Button>
                            </div>
                          </div>
                        </div>

                        {/* Detalhes Expandidos */}
                        <Collapse in={expandedOrders[order.pedido]}>
                          <div>
                            {renderOrderItems(order.pedido)}
                          </div>
                        </Collapse>
                      </Card.Body>
                    </Card>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {renderPagination()}
            </>
          )}
        </Container>
      </Container>

      {/* Warning Modal */}
      <Modal 
        show={showWarningModal} 
        onHide={() => setShowWarningModal(false)}
        centered
      >
        <Modal.Header closeButton className="bg-warning bg-opacity-10 border-0">
          <Modal.Title className="d-flex align-items-center">
            <FaExclamationTriangle className="text-warning me-2" />
            Aviso Importante
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <Alert variant="warning" className="mb-3">
            <FaExclamationTriangle className="me-2" />
            <strong>Atenção!</strong>
          </Alert>
          <p className="mb-3">
            A impressão de etiquetas pode gerar <strong>duplicação de etiquetas na expedição</strong>.
          </p>
          <p className="mb-0">
            Você está prestes a imprimir <strong>{selectedOrders.length} etiqueta(s)</strong>. 
            Verifique se as etiquetas já foram impressas antes de confirmar.
          </p>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button 
            variant="secondary" 
            onClick={() => setShowWarningModal(false)}
            disabled={printingLabels}
          >
            Cancelar
          </Button>
          <Button 
            variant="success" 
            onClick={handleConfirmPrint}
            disabled={printingLabels}
            className="d-flex align-items-center"
          >
            <FaPrint className="me-2" />
            Confirmar Impressão
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .hover-shadow:hover {
          box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
          transform: translateY(-2px);
        }
        
        .page-link {
          cursor: pointer;
        }
        
        .pagination .page-item.active .page-link {
          background-color: #0d6efd;
          border-color: #0d6efd;
        }
      `}</style>
    </>
  );
};

export default PendingOrders;